import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, Input, Table } from 'reactstrap';
import { scopeLabel } from '../utils/concreteEstimatorDesignMix';

function DesignMixPickerModal({
    show,
    loading,
    options = [],
    loadError = '',
    gradeFilter,
    searchText,
    onSearchChange,
    onRefresh,
    onSelect,
    onClose,
    showingAllPlantMixes = false,
}) {
    useEffect(() => {
        if (!show || typeof document === 'undefined') return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [show]);

    if (!show || typeof document === 'undefined') return null;

    const handleSelect = (row, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        onSelect?.(row);
    };

    const handleBackdropMouseDown = (event) => {
        if (event.target === event.currentTarget) {
            onClose?.();
        }
    };

    const modal = (
        <div
            className="estimator-design-mix-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onMouseDown={handleBackdropMouseDown}
        >
            <div
                className="estimator-design-mix-modal-dialog"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="estimator-design-mix-modal-content">
                    <div className="estimator-design-mix-modal-header estimator-recipe-details-header form-control-panel py-2 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <b>Select Design Mix Recipe</b>
                        </h5>
                        <Button type="button" color="secondary" size="sm" onClick={onClose}>
                            Close
                        </Button>
                    </div>
                    <div className="estimator-design-mix-modal-body">
                        <p className="text-muted small mb-2">
                            Showing design mixes for grade <strong>{gradeFilter || '—'}</strong>.
                            Click <strong>Select</strong> on a row (or click the row) to apply the recipe.
                        </p>
                        {showingAllPlantMixes ? (
                            <div className="alert alert-info py-2 small mb-2">
                                No exact match for <strong>{gradeFilter}</strong>. Showing all design mixes for this plant.
                            </div>
                        ) : null}
                        {loadError ? (
                            <div className="alert alert-warning py-2 small mb-2">{loadError}</div>
                        ) : null}
                        <div className="row mb-2">
                            <div className="col-md-10 mb-2">
                                <label className="small font-weight-bold mb-1">Search</label>
                                <Input
                                    bsSize="sm"
                                    value={searchText || ''}
                                    onChange={(e) => onSearchChange?.(e.target.value)}
                                    placeholder="Customer, site, schedule no..."
                                />
                            </div>
                            <div className="col-md-2 mb-2 d-flex align-items-end">
                                <Button
                                    type="button"
                                    color="secondary"
                                    size="sm"
                                    block
                                    disabled={loading}
                                    onClick={onRefresh}
                                >
                                    {loading ? 'Loading…' : 'Refresh'}
                                </Button>
                            </div>
                        </div>
                        <div className="table-responsive estimator-design-mix-picker-table">
                            <Table bordered size="sm" className="estimator-design-mix-table mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: 90 }}>Action</th>
                                        <th>Customer</th>
                                        <th>Site</th>
                                        <th>Grade</th>
                                        <th>Schedule</th>
                                        <th>Mix Details (per m³)</th>
                                        <th>Scope</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && !options.length ? (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted py-3">
                                                Loading design mixes…
                                            </td>
                                        </tr>
                                    ) : null}
                                    {!loading && !options.length ? (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted py-3">
                                                No design mix found for this plant. Create mixes in Quality Control → Design Mix, then click Refresh.
                                            </td>
                                        </tr>
                                    ) : null}
                                    {options.map((row) => (
                                        <tr
                                            key={row.id}
                                            className="estimator-design-mix-picker-row"
                                            onClick={(event) => handleSelect(row, event)}
                                        >
                                            <td>
                                                <Button
                                                    type="button"
                                                    color="primary"
                                                    size="sm"
                                                    onClick={(event) => handleSelect(row, event)}
                                                >
                                                    Select
                                                </Button>
                                            </td>
                                            <td>{row.customer || '—'}</td>
                                            <td>{row.site || '—'}</td>
                                            <td>{row.grade || '—'}</td>
                                            <td>
                                                {[row.work_schedule_no, row.work_schedule_date].filter(Boolean).join(' ') || '—'}
                                            </td>
                                            <td>{row.mix_summary || '—'}</td>
                                            <td>{scopeLabel(row.scope)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}

export default DesignMixPickerModal;
